import React, { useState, useEffect } from 'react';
import Avatar from 'shared_features/avatar';
import styles from './styles.module.scss';

const StaffProfileModal = ({
    isOpen,
    onClose,
    onSave,
    onRemove,
    onPhotoUpload,
    member,
    currentUserRole,
    loading,
    error,
    isUploadingPhoto,
    canEdit,
    canRemove,
}) => {
    const [formData, setFormData] = useState({
        name: '',
        last_name: '',
        title: '',
        role: 'STAFF',
        bio: '',
        instagram: '',
        tiktok: '',
        facebook: '',
        twitter: '',
        photo: '',
    });

    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (member) {
            setFormData({
                name: member.name || '',
                last_name: member.last_name || '',
                title: member.title || '',
                role: member.role || 'STAFF',
                bio: member.bio || '',
                instagram: member.instagram || '',
                tiktok: member.tiktok || '',
                facebook: member.facebook || '',
                twitter: member.twitter || '',
                photo: member.photo || '',
            });
            // Clear validation errors when member changes
            setValidationErrors({});
        }
    }, [member]);

    // Update photo when it changes (after successful upload)
    useEffect(() => {
        if (member?.photo && member.photo !== formData.photo) {
            setFormData(prev => ({ ...prev, photo: member.photo }));
        }
    }, [member?.photo]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Validation and normalization functions for social media handles
    const validateAndNormalizeSocialHandle = (input, platform) => {
        if (!input) return '';

        const trimmed = input.trim();
        if (!trimmed) return '';

        // Define platform URL patterns (with and without protocol)
        const patterns = {
            instagram: [
                /^https?:\/\/(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^https?:\/\/(?:www\.)?ig\.me\/([a-zA-Z0-9._]+)\/?$/,
                /^(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^(?:www\.)?ig\.me\/([a-zA-Z0-9._]+)\/?$/
            ],
            tiktok: [
                /^https?:\/\/(?:www\.)?tiktok\.com\/@([a-zA-Z0-9._]+)\/?$/,
                /^https?:\/\/(?:vm\.)?tiktok\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^(?:www\.)?tiktok\.com\/@([a-zA-Z0-9._]+)\/?$/,
                /^(?:vm\.)?tiktok\.com\/([a-zA-Z0-9._]+)\/?$/
            ],
            facebook: [
                /^https?:\/\/(?:www\.)?facebook\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^https?:\/\/(?:www\.)?fb\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^(?:www\.)?facebook\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^(?:www\.)?fb\.com\/([a-zA-Z0-9._]+)\/?$/
            ],
            twitter: [
                /^https?:\/\/(?:www\.)?twitter\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^https?:\/\/(?:www\.)?x\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^(?:www\.)?twitter\.com\/([a-zA-Z0-9._]+)\/?$/,
                /^(?:www\.)?x\.com\/([a-zA-Z0-9._]+)\/?$/
            ]
        };

        // If it looks like a URL (with or without protocol), try to extract the handle
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://') ||
            trimmed.includes('.com/') || trimmed.includes('.me/')) {
            const platformPatterns = patterns[platform] || [];
            for (const pattern of platformPatterns) {
                const match = trimmed.match(pattern);
                if (match && match[1]) {
                    return match[1];
                }
            }
            // If URL doesn't match expected patterns, return empty
            return '';
        }

        // Remove @ symbol if present
        let handle = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;

        // Validate handle format (alphanumeric, dots, underscores only)
        if (!/^[a-zA-Z0-9._]+$/.test(handle)) {
            return '';
        }

        // Additional length validation (most platforms have limits)
        if (handle.length < 1 || handle.length > 30) {
            return '';
        }

        return handle;
    };

    const handleSocialInputChange = (field, value) => {
        // Update form data immediately
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear any existing validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: null }));
        }

        // Only validate if there's actual input (not empty)
        if (value.trim()) {
            const normalized = validateAndNormalizeSocialHandle(value, field);
            if (!normalized) {
                const platformNames = {
                    instagram: 'Instagram',
                    tiktok: 'TikTok',
                    facebook: 'Facebook',
                    twitter: 'Twitter'
                };
                const platformName = platformNames[field] || field;
                setValidationErrors(prev => ({
                    ...prev,
                    [field]: `Please enter a valid username or ${platformName} URL`
                }));
            }
        }
    };

    const handleBioChange = (value) => {
        // Update form data immediately
        setFormData(prev => ({ ...prev, bio: value }));

        // Clear any existing validation error for bio
        if (validationErrors.bio) {
            setValidationErrors(prev => ({ ...prev, bio: null }));
        }

        // Validate character limit
        if (value.length > 5000) {
            setValidationErrors(prev => ({
                ...prev,
                bio: 'Bio cannot exceed 5,000 characters'
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (canEdit) {
            // Check for validation errors
            const hasErrors = Object.values(validationErrors).some(error => error);
            if (hasErrors) {
                return; // Don't save if there are validation errors
            }

            // Additional check for bio length as a safeguard
            if (formData.bio && formData.bio.length > 5000) {
                setValidationErrors(prev => ({
                    ...prev,
                    bio: 'Bio cannot exceed 5,000 characters'
                }));
                return;
            }

            // Validate and normalize social media handles before saving
            const normalizedData = {
                ...formData,
                instagram: validateAndNormalizeSocialHandle(formData.instagram, 'instagram'),
                tiktok: validateAndNormalizeSocialHandle(formData.tiktok, 'tiktok'),
                facebook: validateAndNormalizeSocialHandle(formData.facebook, 'facebook'),
                twitter: validateAndNormalizeSocialHandle(formData.twitter, 'twitter'),
            };
            onSave(normalizedData);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleRemove = () => {
        if (window.confirm('Are you sure you want to remove this staff member?')) {
            onRemove();
        }
    };

    const getAvailableRoles = () => {
        const allRoles = [
            { value: 'OWNER', label: 'Owner' },
            { value: 'ADMIN', label: 'Admin' },
            { value: 'STAFF', label: 'Staff' }
        ];

        if (member?.account?.isCurrentUser) {
            if (member.role === 'OWNER') {
                return allRoles;
            } else if (member.role === 'ADMIN') {
                return allRoles.filter(role => role.value === 'ADMIN' || role.value === 'STAFF');
            } else {
                return allRoles.filter(role => role.value === 'STAFF');
            }
        } else {
            if (currentUserRole === 'OWNER') {
                return allRoles;
            } else if (currentUserRole === 'ADMIN') {
                return allRoles.filter(role => role.value === 'STAFF');
            } else {
                return [];
            }
        }
    };

    if (!isOpen || !member) return null;

    const availableRoles = getAvailableRoles();

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2>Coaching Staff Profile</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.content}>
                        {error && (
                            <div className={styles.errorMessage}>
                                {error}
                            </div>
                        )}

                        <div className={styles.uploadSection}>
                            <div className={styles.avatarContainer}>
                                <Avatar
                                    imageURL={formData.photo || 'https://profiles.reponestrength.com/default.png'}
                                    uploadEnabled={canEdit}
                                    isSmall={false}
                                    isUploadingAvatar={isUploadingPhoto}
                                    upload={(file) => onPhotoUpload(file)}
                                />
                            </div>
                        </div>

                        <div className={styles.fieldRow}>
                            <div className={styles.field}>
                                <label htmlFor="name">Name</label>
                                <input
                                    id="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    disabled={!canEdit || loading}
                                />
                            </div>

                            <div className={styles.field}>
                                <label htmlFor="last_name">Last Name</label>
                                <input
                                    id="last_name"
                                    type="text"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    disabled={!canEdit || loading}
                                />
                            </div>
                        </div>

                        <div className={styles.fieldRow}>
                            <div className={styles.field}>
                                <label htmlFor="title">Title</label>
                                <input
                                    id="title"
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    disabled={!canEdit || loading}
                                />
                            </div>

                            <div className={styles.field}>
                                <label htmlFor="role">Role</label>
                                <select
                                    id="role"
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    disabled={!canEdit || loading || availableRoles.length === 0}
                                >
                                    {availableRoles.map((roleOption) => (
                                        <option key={roleOption.value} value={roleOption.value}>
                                            {roleOption.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.field}>
                            <label htmlFor="bio">
                                Bio
                                <span className={styles.charCount}>
                                    {formData.bio.length}/5,000
                                </span>
                            </label>
                            <textarea
                                id="bio"
                                value={formData.bio}
                                onChange={(e) => handleBioChange(e.target.value)}
                                rows={6}
                                disabled={!canEdit || loading}
                                className={validationErrors.bio ? styles.inputError : ''}
                            />
                            {validationErrors.bio && (
                                <div className={styles.errorText}>{validationErrors.bio}</div>
                            )}
                        </div>

                        <div className={styles.socialSection}>
                            <h3>Social Links</h3>
                            <div className={styles.socialRow}>
                                <div className={styles.field}>
                                    <label htmlFor="instagram">Instagram</label>
                                    <input
                                        id="instagram"
                                        type="text"
                                        value={formData.instagram}
                                        onChange={(e) => handleSocialInputChange('instagram', e.target.value)}
                                        disabled={!canEdit || loading}
                                        className={validationErrors.instagram ? styles.inputError : ''}
                                    />
                                    {validationErrors.instagram && (
                                        <div className={styles.errorText}>{validationErrors.instagram}</div>
                                    )}
                                </div>

                                <div className={styles.field}>
                                    <label htmlFor="tiktok">TikTok</label>
                                    <input
                                        id="tiktok"
                                        type="text"
                                        value={formData.tiktok}
                                        onChange={(e) => handleSocialInputChange('tiktok', e.target.value)}
                                        disabled={!canEdit || loading}
                                        className={validationErrors.tiktok ? styles.inputError : ''}
                                    />
                                    {validationErrors.tiktok && (
                                        <div className={styles.errorText}>{validationErrors.tiktok}</div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.socialRow}>
                                <div className={styles.field}>
                                    <label htmlFor="facebook">Facebook</label>
                                    <input
                                        id="facebook"
                                        type="text"
                                        value={formData.facebook}
                                        onChange={(e) => handleSocialInputChange('facebook', e.target.value)}
                                        disabled={!canEdit || loading}
                                        className={validationErrors.facebook ? styles.inputError : ''}
                                    />
                                    {validationErrors.facebook && (
                                        <div className={styles.errorText}>{validationErrors.facebook}</div>
                                    )}
                                </div>

                                <div className={styles.field}>
                                    <label htmlFor="twitter">Twitter</label>
                                    <input
                                        id="twitter"
                                        type="text"
                                        value={formData.twitter}
                                        onChange={(e) => handleSocialInputChange('twitter', e.target.value)}
                                        disabled={!canEdit || loading}
                                        className={validationErrors.twitter ? styles.inputError : ''}
                                    />
                                    {validationErrors.twitter && (
                                        <div className={styles.errorText}>{validationErrors.twitter}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.footer}>
                        {canRemove && (
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={handleRemove}
                                disabled={loading}
                            >
                                Remove
                            </button>
                        )}
                        <div className={styles.spacer} />
                        <button
                            type="submit"
                            className={styles.saveButton}
                            disabled={!canEdit || loading}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StaffProfileModal;
